import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiPlus, FiEdit3, FiTrash2, FiSave, FiX } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import BottomTabMenu from '../../components/UI/BottomTabMenu';
import { vendedoresService } from '../../services';

const VendedorNotas = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [notas, setNotas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingNota, setEditingNota] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'general'
  });

  useEffect(() => {
    loadNotas();
  }, [id]);

  const loadNotas = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await vendedoresService.getNotas(id);

      if (result.success) {
        setNotas(result.data);
      } else {
        setError(result.message || 'Error al cargar las notas');
        setNotas([]);
      }
    } catch (err) {
      console.error('Error cargando notas:', err);
      setError('Error de conexión al cargar las notas');
      setNotas([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      if (editingNota) {
        // Actualizar nota existente
        const result = await vendedoresService.updateNota(id, editingNota.id, formData);

        if (result.success) {
          await loadNotas(); // Recargar notas
        } else {
          setError(result.message || 'Error al actualizar la nota');
          return;
        }
      } else {
        // Crear nueva nota
        const result = await vendedoresService.createNota(id, formData);

        if (result.success) {
          await loadNotas(); // Recargar notas
        } else {
          setError(result.message || 'Error al crear la nota');
          return;
        }
      }

      // Resetear formulario
      setFormData({ title: '', content: '', type: 'general' });
      setShowForm(false);
      setEditingNota(null);
    } catch (err) {
      console.error('Error guardando nota:', err);
      setError('Error de conexión al guardar la nota');
    }
  };

  const handleEdit = (nota) => {
    setEditingNota(nota);
    setFormData({
      title: nota.title,
      content: nota.content,
      type: nota.type
    });
    setShowForm(true);
  };

  const handleDelete = async (notaId) => {
    if (window.confirm('¿Estás seguro de eliminar esta nota?')) {
      setError(null);

      try {
        const result = await vendedoresService.deleteNota(id, notaId);

        if (result.success) {
          await loadNotas(); // Recargar notas
        } else {
          setError(result.message || 'Error al eliminar la nota');
        }
      } catch (err) {
        console.error('Error eliminando nota:', err);
        setError('Error de conexión al eliminar la nota');
      }
    }
  };

  const handleCancel = () => {
    setFormData({ title: '', content: '', type: 'general' });
    setShowForm(false);
    setEditingNota(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTipoLabel = (tipo) => {
    const tipos = {
      general: { label: 'General', color: '#6c757d' },
      seguimiento: { label: 'Seguimiento', color: '#0d6efd' },
      importante: { label: 'Importante', color: '#dc3545' },
      recordatorio: { label: 'Recordatorio', color: '#ffc107' }
    };
    return tipos[tipo] || tipos.general;
  };

  if (isLoading) {
    return <LoadingGrid message="Cargando notas..." />;
  }

  return (
    <div className="vendedor-notas-page" style={{ paddingBottom: '100px' }}>
      {/* Error message */}
      {error && (
        <div className="alert alert--danger" style={{ margin: '20px' }}>
          <strong>Error:</strong> {error}
          <button
            className="btn btn--ghost btn--sm"
            onClick={loadNotas}
            style={{ marginLeft: '10px' }}
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Header */}
      <div className="form-header">
        <button
          className="btn btn--ghost"
          onClick={() => navigate('/system/vendedores')}
          type="button"
        >
          <FiArrowLeft /> Volver a Vendedores
        </button>

        <h1 className="form-title">
          NOTAS DEL VENDEDOR
        </h1>

        <div className="form-actions">
          <button
            className="btn btn--primary"
            onClick={() => setShowForm(true)}
            type="button"
          >
            <FiPlus /> Nueva Nota
          </button>
        </div>
      </div>

      {/* Formulario de nota */}
      {showForm && (
        <div className="note-form-container" style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ marginBottom: '20px' }}>
            {editingNota ? 'Editar Nota' : 'Nueva Nota'}
          </h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Título</label>
              <input
                type="text"
                className="form-input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Contenido</label>
              <textarea
                className="form-input"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={4}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Tipo</label>
              <select
                className="form-input"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              >
                <option value="general">General</option>
                <option value="seguimiento">Seguimiento</option>
                <option value="importante">Importante</option>
                <option value="recordatorio">Recordatorio</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={handleCancel}
              >
                <FiX /> Cancelar
              </button>
              <button
                type="submit"
                className="btn btn--primary"
              >
                <FiSave /> Guardar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de notas */}
      <div className="notes-list">
        {notas.length === 0 ? (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '40px',
            textAlign: 'center',
            color: '#999'
          }}>
            <p>No hay notas registradas para este vendedor</p>
          </div>
        ) : (
          notas.map((nota) => (
            <div
              key={nota.id}
              className="note-card"
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '15px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                borderLeft: `4px solid ${getTipoLabel(nota.type).color}`
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px' }}>{nota.title}</h3>
                    <span
                      style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        backgroundColor: `${getTipoLabel(nota.type).color}20`,
                        color: getTipoLabel(nota.type).color
                      }}
                    >
                      {getTipoLabel(nota.type).label}
                    </span>
                  </div>
                  <p style={{ color: '#666', marginBottom: '10px' }}>{nota.content}</p>
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    <span>Por: {nota.created_by}</span>
                    <span style={{ margin: '0 8px' }}>•</span>
                    <span>{formatDate(nota.created_at)}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="btn btn--icon btn--ghost"
                    onClick={() => handleEdit(nota)}
                    title="Editar"
                  >
                    <FiEdit3 size={16} />
                  </button>
                  <button
                    className="btn btn--icon btn--ghost btn--danger"
                    onClick={() => handleDelete(nota.id)}
                    title="Eliminar"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Menú inferior */}
      <BottomTabMenu
        baseRoute={`/system/vendedores/${id}`}
        currentTab="notas"
        tabs={[
          { id: 'datos', label: 'Datos', route: '' },
          { id: 'ventas', label: 'Reporte de Ventas', route: '/ventas' },
          { id: 'comisiones', label: 'Reporte de Comisiones', route: '/comisiones' },
          { id: 'clientes', label: 'Lista de Clientes', route: '/clientes' },
          { id: 'grafica', label: 'Gráfica de Ventas', route: '/grafica' },
          { id: 'notas', label: 'Notas', route: '/notas' }
        ]}
      />
    </div>
  );
};

export default VendedorNotas;
